import { useState } from "react";
import { Camera, Loader2, Monitor, ScanEye, Smartphone, Archive, AlertTriangle, ListChecks, Images } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Sheet, SheetContent, SheetDescription, SheetTitle } from "@/components/ui/sheet";
import { Pick } from "@/components/settings/parts";
import ConfirmDialog from "@/components/common/ConfirmDialog";
import CostButton from "@/components/ai/CostButton";
import StopButton from "@/components/ai/StopButton";
import { usd } from "@/components/ai/useAiEdits";
import { useEditor } from "@/components/editor/useEditorState";
import { BUSY, useVisualReview } from "./useVisualReview";
import RenderGallery from "./RenderGallery";
import DefectsStep from "./DefectsStep";
import FixStep from "./FixStep";
import ReviewPrompt from "./ReviewPrompt";
import { RuleProposals } from "./VisualRules";
import { STATUS, STEPS, stepOf } from "./reviewText";

const SCOPES = [["main", "Home e pagine del menu (max 6)"], ["full", "Tutte le pagine"]];

function Stepper({ status }) {
  const at = stepOf(status);
  return (
    <ol className="flex flex-wrap gap-x-5 gap-y-1 text-xs" data-testid="review-stepper">
      {STEPS.map((s, i) => (
        <li key={s.key} className={`flex items-center gap-1.5 ${i < at ? "text-emerald-700" : i === at ? "text-slate-900 font-semibold" : "text-slate-400"}`}>
          <span className={`grid h-5 w-5 place-items-center rounded-full text-[10px] ${i < at ? "bg-emerald-100" : i === at ? "bg-slate-900 text-white" : "bg-slate-100"}`}>{i + 1}</span>
          {s.label}<span className="font-normal text-slate-400">· {s.note}</span>
        </li>
      ))}
    </ol>
  );
}

function Start({ hook, list }) {
  const [scope, setScope] = useState(list?.settings?.scope || "main");
  const ok = list?.ok;
  return (
    <div className="max-w-2xl space-y-4" data-testid="review-start">
      <p className="text-sm text-slate-700">Lo Studio compila il sito, scatta screenshot reali su desktop (1440px) e mobile (390px) e misura logo, immagini e overflow.
        Poi, solo se confermi la stima, l'AI li analizza come un art director e propone correzioni sezione per sezione: le vedi prima e dopo e decidi tu cosa applicare.</p>
      <div className="grid gap-2 sm:grid-cols-3 text-xs">
        {[[Camera, "Screenshot e misure", "gratis, 1-2 minuti"], [ScanEye, "Analisi AI", "stima e conferma"], [ListChecks, "Correzioni", "approvazione manuale"]].map(([I, a, b]) => (
          <div key={a} className="rounded-lg border border-slate-200 bg-white p-3"><I size={15} className="text-sky-700" /><p className="mt-1.5 font-medium text-slate-900">{a}</p><p className="text-slate-500">{b}</p></div>
        ))}
      </div>
      {!ok && list && <p className="flex items-center gap-1.5 text-sm text-amber-800" data-testid="review-tools-unavailable"><AlertTriangle size={14} />{list.message}</p>}
      {!ok && list && /Non disponibile su questo server/.test(list.message || "") && <p className="text-xs text-slate-600" data-testid="review-tools-fallback">Nel frattempo: controlla le pagine con l&apos;anteprima dell&apos;editor in vista desktop e mobile e apri il link di anteprima su un telefono reale. Gli avvisi dell&apos;ultima generazione restano disponibili con il pulsante «Avvisi dell&apos;ultima generazione» in basso a sinistra nell&apos;editor.</p>}
      <div className="flex flex-wrap items-center gap-2">
        <Pick value={scope} onChange={setScope} options={SCOPES} testid="review-scope-select" className="w-72" />
        <Button onClick={() => hook.start(scope)} disabled={!ok || hook.busy === "start"} className="bg-sky-600 hover:bg-sky-700" data-testid="review-start-btn">
          {hook.busy === "start" ? <Loader2 size={14} className="mr-1.5 animate-spin" /> : <Camera size={14} className="mr-1.5" />}Prepara screenshot (gratis)
        </Button>
      </div>
      <p className="text-[11px] text-slate-500">Tetto di spesa per revisione: {usd(list?.settings?.max_usd)} (modificabile in Impostazioni › Revisione visiva).</p>
    </div>
  );
}

function Running({ rev, label }) {
  const { api, id } = useEditor();
  const paid = rev.status !== "rendering";
  return (
    <div className="flex flex-wrap items-center justify-between gap-3 rounded-xl border border-sky-200 bg-sky-50 p-4" data-testid="review-running">
      <p className="flex items-center gap-2 text-sm text-sky-900"><Loader2 size={15} className="animate-spin" />{label}</p>
      {paid && <StopButton api={api} projectId={id} testid="review-stop-btn" />}
    </div>
  );
}

function Body({ rev, hook, list, view, setView }) {
  if (!rev || ["discarded", "error"].includes(rev.status)) {
    return (
      <>
        {rev?.status === "error" && <p className="mb-4 rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid="review-error">{rev.error}</p>}
        <Start hook={hook} list={list} />
      </>
    );
  }
  if (rev.status === "rendering") return <Running rev={rev} label="Compilo il sito e scatto gli screenshot desktop e mobile… (1-2 minuti, gratis)" />;
  const err = rev.error && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="review-step-error">{rev.error}</p>;
  if (rev.status === "fixing") return <div className="space-y-3">{err}<Running rev={rev} label="L'AI prepara le correzioni, poi scatto gli screenshot «dopo»…" /></div>;
  const left = rev.pages_total - rev.pages_analyzed;
  const showFix = ["ready", "applied", "undone"].includes(rev.status) && view !== "defects";
  return (
    <div className="space-y-4">
      {err}
      {rev.stale && <p className="rounded-lg border border-amber-200 bg-amber-50 p-3 text-xs text-amber-900" data-testid="review-stale">Il progetto è cambiato dopo questi screenshot: per un giudizio aggiornato prepara nuovi screenshot (gratis).</p>}
      {rev.status === "analyzing" && <Running rev={rev} label={`Analisi AI in corso: ${rev.pages_analyzed} di ${rev.pages_total} pagine…`} />}
      {["rendered", "analyzed"].includes(rev.status) && left > 0 && (
        <div className="max-w-md rounded-xl border border-slate-200 bg-white p-4" data-testid="review-analyze-box">
          <p className="mb-2 text-sm font-medium text-slate-900">{rev.pages_analyzed ? `Analizza le ${left} pagine rimanenti` : `Analizza ${left} pagine con l'AI`}</p>
          <CostButton path={`/visual-reviews/${rev.id}/analyze/estimate`} body={{ n: rev.pages_analyzed, spent: rev.spent_usd }} label="Analizza screenshot"
            busy={hook.busy === "analyze"} onRun={hook.analyze} testid="review-analyze-btn" />
        </div>
      )}
      {["ready", "applied", "undone"].includes(rev.status) && (
        <div className="flex gap-1 rounded-lg border bg-white p-1 w-fit" data-testid="review-view-toggle">
          {[["fix", "Prima / dopo"], ["defects", "Difetti e nuove correzioni"]].map(([k, l]) => (
            <button key={k} onClick={() => setView(k)} className={`rounded-md px-3 py-1.5 text-xs ${(view === k || (k === "fix" && view !== "defects")) ? "bg-slate-900 text-white" : "text-slate-600"}`} data-testid={`review-view-${k}`}>{l}</button>
          ))}
        </div>
      )}
      {showFix ? <FixStep rev={rev} hook={hook} /> : rev.pages_analyzed > 0 && <DefectsStep rev={rev} hook={hook} />}
      {!showFix && (
        <details className="group" open={!rev.pages_analyzed} data-testid="review-gallery-toggle">
          <summary className="cursor-pointer text-sm font-medium text-slate-800 inline-flex items-center gap-1.5"><Images size={14} />Screenshot e misure</summary>
          <div className="mt-3"><RenderGallery rev={rev} /></div>
        </details>
      )}
    </div>
  );
}

export default function VisualReviewSheet() {
  const { ui, setUi } = useEditor();
  const hook = useVisualReview(ui.review);
  const { rev, list } = hook;
  const [view, setView] = useState("fix");
  const [askDiscard, setAskDiscard] = useState(false);
  const active = rev && !["discarded", "error"].includes(rev.status);
  return (
    <>
    {!ui.review && <ReviewPrompt rev={rev} onOpen={() => setUi({ review: true })} />}
    <Sheet open={!!ui.review} onOpenChange={(o) => setUi({ review: o })}>
      <SheetContent side="right" className="p-0 w-[min(1180px,100vw)] sm:max-w-none flex flex-col bg-slate-50" data-testid="review-sheet">
        <div className="border-b border-slate-200 bg-white px-5 py-4 space-y-3">
          <div className="flex flex-wrap items-start justify-between gap-3 pr-8">
            <div>
              <SheetTitle className="flex items-center gap-2 text-lg"><ScanEye size={18} className="text-sky-700" />Revisione visiva</SheetTitle>
              <SheetDescription className="text-xs">Controllo qualità su screenshot reali <Monitor size={11} className="inline" /> <Smartphone size={11} className="inline" /> · nessuna correzione viene applicata senza la tua approvazione.</SheetDescription>
            </div>
            {active && (
              <div className="flex flex-wrap items-center gap-2 text-xs">
                <span className="rounded-full bg-slate-100 px-2.5 py-1 font-medium text-slate-700" data-testid="review-status">{STATUS[rev.status]}</span>
                <span className="rounded-full border border-slate-200 bg-white px-2.5 py-1 text-slate-600 tabular-nums" data-testid="review-spent">Speso {usd(rev.spent_usd)} di {usd(rev.budget_usd)}</span>
                {!BUSY.includes(rev.status) && (
                  <>
                    <Button size="sm" variant="outline" className="h-7 text-xs" onClick={() => hook.start(rev.scope)} disabled={hook.busy === "start"} data-testid="review-new-shots-btn"><Camera size={12} className="mr-1" />Nuovi screenshot</Button>
                    <Button size="sm" variant="ghost" className="h-7 text-xs" onClick={() => setAskDiscard(true)} data-testid="review-discard-btn"><Archive size={12} className="mr-1" />Archivia</Button>
                  </>
                )}
              </div>
            )}
          </div>
          {active && <Stepper status={rev.status} />}
        </div>
        <div className="flex-1 overflow-y-auto px-5 py-5 space-y-4" data-testid="review-body">
          {ui.review && <RuleProposals dep={`${rev?.id}:${rev?.status}`} />}
          {list === null ? <Loader2 size={16} className="animate-spin text-slate-400" /> : <Body rev={rev} hook={hook} list={list} view={view} setView={setView} />}
        </div>
      </SheetContent>
    </Sheet>
    <ConfirmDialog open={askDiscard} onOpenChange={setAskDiscard} destructive confirmLabel="Archivia" title="Archiviare questa revisione?"
      description="Screenshot, analisi e proposte non saranno più mostrati qui (i difetti restano nelle statistiche delle regole apprese). Per un nuovo controllo servono nuovi screenshot e una nuova analisi AI a pagamento."
      onConfirm={() => { setAskDiscard(false); hook.discard(); }} />
    </>
  );
}
